import { c as _c } from "react/compiler-runtime";
import type { PublicTraceEntry } from '@onefootprint/request-types/dashboard';
import { LinkButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import useNodeContext from '@/components/playbook-builder/hooks/use-node-context';
import useSidebar from '@/components/playbook-builder/hooks/use-sidebar';
import { getTraceHighlightStyles } from '@/components/playbook-builder/utils/get-trace-highlight-styles';
import type { FormData } from '../conditional-form';
import CustomBadge from './custom-badge';
import EditableBranchName from './editable-branch-name';
type BaseBranchProps = {
  trace?: PublicTraceEntry;
  canEdit: boolean;
  children?: React.ReactNode;
  branchIndex: number | 'default';
  className?: string;
};
export const BaseBranch = t0 => {
  const $ = _c(44);
  const {
    trace,
    canEdit,
    children,
    branchIndex,
    className
  } = t0;
  const {
    isAnyComponentDirty
  } = useDirty();
  const {
    t
  } = useTranslation("playbook-builder");
  const {
    watch
  } = useFormContext();
  const {
    open: openNode
  } = useSidebar();
  let branchField;
  if (branchIndex === "default") {
    branchField = "defaultBranch";
  } else {
    branchField = `branches.${branchIndex}.branch`;
  }
  let t1;
  if ($[0] !== branchField || $[1] !== watch) {
    t1 = watch(branchField);
    $[0] = branchField;
    $[1] = watch;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const branch = t1;
  const {
    node: successor
  } = useNodeContext(branch.successor);
  const wasBranchExecuted = trace?.context?.kind === "conditional" && trace?.context?.outcomeBranchName === branch.name && branch.name !== "default";
  let isViewBranchDisabled;
  let t2;
  if ($[3] !== isAnyComponentDirty || $[4] !== successor?.kind || $[5] !== wasBranchExecuted) {
    const borderClass = getTraceHighlightStyles(wasBranchExecuted);
    isViewBranchDisabled = isAnyComponentDirty || successor?.kind === "placeholder";
    t2 = cx(["border overflow-hidden", borderClass, "rounded"]);
    $[3] = isAnyComponentDirty;
    $[4] = successor?.kind;
    $[5] = wasBranchExecuted;
    $[6] = isViewBranchDisabled;
    $[7] = t2;
  } else {
    isViewBranchDisabled = $[6];
    t2 = $[7];
  }
  let t3;
  if ($[8] !== className) {
    t3 = cx(["flex items-center justify-between gap-1 bg-secondary h-[48px] py-2 px-4 relative", className]);
    $[8] = className;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== branchIndex || $[11] !== t) {
    t4 = branchIndex === "default" ? t(_temp) : t(_temp2);
    $[10] = branchIndex;
    $[11] = t;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t4) {
    t5 = <span className="text-tertiary">{t4}</span>;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const t6 = `${branchField}.name`;
  let t7;
  if ($[15] !== canEdit || $[16] !== t6) {
    t7 = <EditableBranchName canEdit={canEdit} fieldName={t6} />;
    $[15] = canEdit;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t5 || $[19] !== t7) {
    t8 = <div className="text-label-2 text-primary flex items-center gap-2 w-full whitespace-nowrap max-md:min-w-0">{t5}{t7}</div>;
    $[18] = t5;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== branch.successor || $[22] !== openNode) {
    t9 = () => openNode(branch.successor);
    $[21] = branch.successor;
    $[22] = openNode;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t) {
    t10 = t(_temp3);
    $[24] = t;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== isViewBranchDisabled || $[27] !== t10 || $[28] !== t9) {
    t11 = <LinkButton onClick={t9} disabled={isViewBranchDisabled}>{t10}</LinkButton>;
    $[26] = isViewBranchDisabled;
    $[27] = t10;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11 || $[31] !== t8) {
    t12 = <div className="flex flex-row justify-between gap-1 w-full max-md:min-w-0">{t8}{t11}</div>;
    $[30] = t11;
    $[31] = t8;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t || $[34] !== wasBranchExecuted) {
    t13 = wasBranchExecuted && <CustomBadge>{t(_temp4)}</CustomBadge>;
    $[33] = t;
    $[34] = wasBranchExecuted;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t12 || $[37] !== t13 || $[38] !== t3) {
    t14 = <header className={t3}>{t12}{t13}</header>;
    $[36] = t12;
    $[37] = t13;
    $[38] = t3;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== children || $[41] !== t14 || $[42] !== t2) {
    t15 = <section className={t2}>{t14}{children}</section>;
    $[40] = children;
    $[41] = t14;
    $[42] = t2;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  return t15;
};
export default BaseBranch;
function _temp($) {
  return $.rulesNode.defaultBranch.title;
}
function _temp2($_0) {
  return $_0.rulesNode.branch.title;
}
function _temp3($_1) {
  return $_1.rulesNode.navigation.viewBranch;
}
function _temp4($_2) {
  return $_2.rules.branchExecuted;
}